<template>
  <v-navigation-drawer
    v-model="internalValue"
    app
    disable-resize-watcher
    height="100%"
    style="max-width: 400px"
    width="calc(100% - 24px)"
    overlay-color="grey-darken-4"
    :overlay-opacity="0.6"
    :touchless="shouldTouchless"
  >
    <template #prepend ref="lol">
      <navigator-menu-bar
        :hide-back-button="isCategoryRoot"
        @click:close="internalValue = false"
        @click:back="popNavigation"
      ></navigator-menu-bar>

      <div
        class="text-uppercase font-weight-bold menu-link pl-9 pr-4 pt-4 pb-3"
      >
        <span v-if="isCategoryRoot">{{ $t('commons.categories') }}</span>
        <span
          v-else
          class="d-flex flex-column"
          role="button"
          @click="handleListClick(currentLevel.parentCategory, true)"
          >{{ currentLevel.parentCategory.translated.name }}
        </span>
      </div>
    </template>

    <div class="px-7 pb-4">
      <div
        v-if="!isCategoryRoot"
        role="listbox"
        class="d-flex align-center px-2 mb-1"
        style="min-height: 40px; cursor: pointer; user-select: none"
        @click="handleListClick(currentLevel.parentCategory, true)"
      >
        <base-avatar size="40px" class="mr-4 my-2">
          <base-icon
            mdi="arrow-left"
            style="transform: rotate(180deg)"
          ></base-icon>
        </base-avatar>
        <div class="text-body-3 font-weight-bold">
          {{ $t('navigationDrawer.viewAll') }}
        </div>
      </div>

      <div
        v-for="category in currentCategories"
        :key="category.id"
        role="listbox"
        class="d-flex align-center px-2 mb-1"
        style="min-height: 40px; cursor: pointer; user-select: none"
        @click="handleListClick(category)"
      >
        <base-avatar
          v-if="!isCategoryRoot && category.media"
          size="40px"
          class="mr-4 my-2"
        >
          <base-image
            :media="category.media"
            :init-width="40"
            height="40"
            width="40"
            loading="lazy"
            alt=""
          />
        </base-avatar>
        <div class="menu-link">
          {{ category.translated.name }}
          <base-icon
            v-if="category.children.length"
            x-small
            mdi="chevron-right"
          ></base-icon>
        </div>
      </div>
    </div>

    <template v-if="isCategoryRoot">
      <template v-if="$config.featureFlags.isBsMbSwitchEnabled">
        <navigator-highlight-box
          v-if="$config.isBsDeBuild"
          :url="$config.mabuKidsDeUrl"
          :src="require('~/assets/images/navigation/mb_nav_mobil_banner.jpg')"
          text-class="lilac-dark white--text"
        >
          {{ $t('commons.mabuKidsIntro') }}
        </navigator-highlight-box>

        <navigator-highlight-box
          v-if="$config.isMaBuBuild"
          :url="$config.babySweetsDeUrl"
          :src="require('~/assets/images/navigation/bs_nav_mobil_banner.jpg')"
          text-class="pink-berry white--text"
        >
          {{ $t('commons.bsIntro') }}
        </navigator-highlight-box>
      </template>
      <navigator-highlight-box
        :url="$t('links.themenweltenFruehlingSommer')"
        :src="
          require('~/assets/images/navigation/nav_mobil_banner_fruehling_V2.jpg')
        "
        text-class="apricot-dark white--text"
      >
        {{ $t('commons.inspirationSpring') }}
      </navigator-highlight-box>

      <div class="pa-7 pt-4 d-flex flex-column">
        <nuxt-link
          v-for="footer in footerList"
          :key="footer.routePath"
          :to="localePath(footer.routePath)"
          class="
            mb-1
            px-2
            text-body-3 text-decoration-none
            grey-darken-4--text
            d-flex
            align-center
          "
          style="min-height: 40px"
        >
          {{ footer.routeLabel }}
        </nuxt-link>
      </div>

      <footer-contact-info class="mb-5 text-body-3">
        <footer-social-icons></footer-social-icons>
        <div class="text-h5 mt-9 mb-7">{{ $t('commons.contact') }}</div>
      </footer-contact-info>
    </template>

    <template #append>
      <navigator-drawer-action-icons
        v-if="shouldShowActionBar"
        :categories="currentLevel.categories"
        @click:btn="handleListClick($event, true)"
      ></navigator-drawer-action-icons>
      <navigator-drawer-navigation-buttons
        :hide-back-button="!isCategoryRoot"
        @click:close="internalValue = false"
        @click:back="popNavigation"
      ></navigator-drawer-navigation-buttons>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import { watch } from '@nuxtjs/composition-api'
import {
  useCategoryPager,
  useFooterNavigation,
  useMainNavigation,
  useUIState,
} from '~/composables'
import { CART_SIDEBAR_STATE } from '~/services/UiStates'
import {
  getCategoryUrl,
  isCategoryHidden,
  CATEGORY_HIDE_TYPE_MOBILE,
  isCategoryCustomFieldEnabled,
} from '~/helpers'
import {
  ATTRIBUTE_CATEGORY_SHOW_ACTION_BAR,
  ATTRIBUTE_FOOTER_CATEGORY_SHOW_IN_DRAWER,
} from '~/services/cfAttributes'
import FooterContactInfo from '~/components/footer/FooterContactInfo'
import NavigatorDrawerActionIcons from '~/components/navigator-drawer/NavigatorDrawerActionIcons'
import NavigatorDrawerNavigationButtons from '~/components/navigator-drawer/NavigatorDrawerNavigationButtons'
import FooterSocialIcons from '~/components/footer/FooterSocialIcons'
import BaseIcon from '~/components/base/icon/BaseIcon'
import BaseAvatar from '~/components/base/avatar/BaseAvatar'
import NavigatorMenuBar from '~/components/navigator-drawer/NavigatorMenuBar'
import BaseImage from '~/components/base/image/BaseImage'
import NavigatorHighlightBox from '~/components/navigator-drawer/NavigatorHighlightBox'

// TODO: MB Highlight Box sollte bei der App ausgeblendet werden
export default {
  name: 'NavigatorDrawer',
  components: {
    NavigatorHighlightBox,
    BaseImage,
    NavigatorMenuBar,
    BaseAvatar,
    BaseIcon,
    FooterSocialIcons,
    NavigatorDrawerNavigationButtons,
    NavigatorDrawerActionIcons,
    FooterContactInfo,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initNavUrl: {
      type: String,
      default: null,
    },
  },
  setup(props, { root, emit }) {
    const { isOpen: isCartOpen } = useUIState(root, CART_SIDEBAR_STATE)
    const { navigationElements } = useMainNavigation(root)
    const { navigationLinks: footerElements } = useFooterNavigation(root)
    const { navigationLinks } = useFooterNavigation(root)
    const booted = ref(false)
    const navigationLevels = ref([])

    const internalValue = computed({
      get() {
        return booted.value ? props.value : false
      },
      set(value) {
        emit('input', value)
      },
    })

    onMounted(() => {
      setTimeout(() => (booted.value = true), 5)
    })

    const { getTechnicalPath, CAT_TECHNICAL_NAME_PERSONALISATION } =
      useCategoryPager(root)

    const findNavigationPath = (navigations, findId, path = []) => {
      for (const nav of navigations) {
        const foundSeoUrl = nav.seoUrls.find(
          ({ seoPathInfo }) =>
            findId ===
            (seoPathInfo?.startsWith('/') ? seoPathInfo : `/${seoPathInfo}`)
        )
        if (
          nav.id === findId ||
          !!foundSeoUrl ||
          findNavigationPath(nav.children, findId, path)
        ) {
          path.push(nav)

          return true
        }
      }

      return false
    }

    watch(internalValue, (isOpen) => {
      if (isOpen && props.initNavUrl) {
        const path = []

        findNavigationPath(
          navigationElements.value || [],
          props.initNavUrl,
          path
        )

        navigationLevels.value = path.reverse().map((cat) => {
          return {
            categories: cat.children,
            parentCategory: cat,
          }
        })

        emit('opened')
      }
    })

    return {
      navigationElements,
      navigationLinks,
      internalValue,
      footerElements,
      isCartOpen,
      getTechnicalPath,
      CAT_TECHNICAL_NAME_PERSONALISATION,
      navigationLevels,
    }
  },
  computed: {
    currentLevel() {
      return this.navigationLevels.length
        ? this.navigationLevels[this.navigationLevels.length - 1]
        : {
            parentCategory: null,
            categories: this.navigations,
          }
    },
    footerList() {
      return this.filterFooterNavigations(this.footerElements || [])
    },
    navigations() {
      return this.navigationElements || []
    },
    isCategoryRoot() {
      return this.currentLevel.parentCategory === null
    },
    shouldShowActionBar() {
      return isCategoryCustomFieldEnabled(
        this.currentLevel.parentCategory,
        ATTRIBUTE_CATEGORY_SHOW_ACTION_BAR
      )
    },
    currentCategories() {
      return this.currentLevel.categories.filter((category) => {
        return !isCategoryHidden(category, CATEGORY_HIDE_TYPE_MOBILE)
      })
    },
    shouldTouchless() {
      return this.isCartOpen
    },
  },
  methods: {
    filterFooterNavigations(navigations) {
      const navs = []

      navigations.forEach((navigation) => {
        navigation.children.forEach((childCategory) => {
          if (
            isCategoryCustomFieldEnabled(
              childCategory,
              ATTRIBUTE_FOOTER_CATEGORY_SHOW_IN_DRAWER
            )
          ) {
            navs.push(childCategory)
          }
        })
      })

      return navs
    },
    handleListClick(category, forcePush = false) {
      if (!forcePush && category.children.length) {
        this.navigationLevels.push({
          parentCategory: category,
          categories: category.children,
        })

        this.scrollToTop()
      } else {
        this.$router.push(this.localePath(getCategoryUrl(category)))
        this.internalValue = false
      }
    },
    popNavigation() {
      this.navigationLevels.pop()
      this.scrollToTop()
    },
    scrollToTop() {
      this.$el.querySelector('.v-navigation-drawer__content').scrollTo(0, 0)
    },
  },
}
</script>
